import { useState } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import { Phone, Mail, Calendar, FileText, CheckSquare, Plus, Loader2 } from 'lucide-react'
import { activityApi } from '@/lib/api'
import { getApiErrorMessage } from '@/lib/utils'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import { Label } from '@/components/ui/label'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs'
import type { ActivityType, CRMEntityType, CreateActivityRequest, ActivityPriority } from '@/types'

interface QuickActivityLogProps {
  entityType: CRMEntityType
  entityId: string
  onSuccess?: () => void
  trigger?: React.ReactNode
  defaultActivityType?: ActivityType
}

const activityTypes: { type: ActivityType; icon: React.ElementType }[] = [
  { type: 'CALL', icon: Phone },
  { type: 'EMAIL', icon: Mail },
  { type: 'MEETING', icon: Calendar },
  { type: 'NOTE', icon: FileText },
  { type: 'TASK', icon: CheckSquare },
]

export function QuickActivityLog({
  entityType,
  entityId,
  onSuccess,
  trigger,
  defaultActivityType = 'CALL',
}: QuickActivityLogProps) {
  const { t } = useTranslation()
  const queryClient = useQueryClient()
  const [open, setOpen] = useState(false)
  const [activeTab, setActiveTab] = useState<ActivityType>(defaultActivityType)
  const [formData, setFormData] = useState<Partial<CreateActivityRequest>>({})

  const createMutation = useMutation({
    mutationFn: (data: CreateActivityRequest) => activityApi.createActivity(data),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['timeline', entityType, entityId] })
      queryClient.invalidateQueries({ queryKey: ['activities'] })
      // If it's a task, also invalidate tasks queries
      if (variables.activity_type === 'TASK') {
        queryClient.invalidateQueries({ queryKey: ['tasks'] })
      }
      toast.success(t('crm.activityCreated', 'Activity logged successfully'))
      setOpen(false)
      setFormData({})
      onSuccess?.()
    },
    onError: (error) => {
      toast.error(getApiErrorMessage(error, t('messages.createError', 'Failed to create')))
    },
  })

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    if (!formData.subject) {
      toast.error(t('validation.required', 'Subject is required'))
      return
    }

    createMutation.mutate({
      activity_type: activeTab,
      entity_type: entityType,
      entity_id: entityId,
      subject: formData.subject,
      description: formData.description,
      activity_date: formData.activity_date || new Date().toISOString(),
      duration_minutes: formData.duration_minutes,
      due_date: formData.due_date,
      priority: formData.priority,
      call_direction: formData.call_direction,
      call_outcome: formData.call_outcome,
      email_direction: formData.email_direction,
      location: formData.location,
      meeting_link: formData.meeting_link,
    })
  }

  const resetForm = () => {
    setFormData({})
  }

  return (
    <Dialog open={open} onOpenChange={(isOpen) => { setOpen(isOpen); if (!isOpen) resetForm() }}>
      <DialogTrigger asChild>
        {trigger || (
          <Button size="sm">
            <Plus className="h-4 w-4 mr-2" />
            {t('crm.logActivity', 'Log Activity')}
          </Button>
        )}
      </DialogTrigger>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>{t('crm.logActivity', 'Log Activity')}</DialogTitle>
          <DialogDescription>
            {t('crm.logActivityDescription', 'Record an interaction or create a task')}
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit}>
          <Tabs value={activeTab} onValueChange={(v) => { setActiveTab(v as ActivityType); resetForm() }}>
            <TabsList className="grid w-full grid-cols-5">
              {activityTypes.map(({ type, icon: Icon }) => (
                <TabsTrigger key={type} value={type} className="flex flex-col gap-1 py-2">
                  <Icon className="h-4 w-4" />
                  <span className="text-xs">{type}</span>
                </TabsTrigger>
              ))}
            </TabsList>

            {/* Common fields for all activity types */}
            <div className="space-y-4 mt-4">
              <div className="space-y-2">
                <Label htmlFor="subject">{t('crm.subject', 'Subject')} *</Label>
                <Input
                  id="subject"
                  value={formData.subject || ''}
                  onChange={(e) => setFormData({ ...formData, subject: e.target.value })}
                  placeholder={t('crm.subjectPlaceholder', 'Enter subject...')}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="description">{t('crm.description', 'Description')}</Label>
                <Textarea
                  id="description"
                  value={formData.description || ''}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  placeholder={t('crm.descriptionPlaceholder', 'Add details...')}
                  rows={3}
                />
              </div>

              {/* Call-specific fields */}
              {activeTab === 'CALL' && (
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>{t('crm.callDirection', 'Direction')}</Label>
                    <Select
                      value={formData.call_direction || ''}
                      onValueChange={(v) => setFormData({ ...formData, call_direction: v as 'INBOUND' | 'OUTBOUND' })}
                    >
                      <SelectTrigger><SelectValue placeholder="Select..." /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="INBOUND">Inbound</SelectItem>
                        <SelectItem value="OUTBOUND">Outbound</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>{t('crm.duration', 'Duration (min)')}</Label>
                    <Input
                      type="number"
                      value={formData.duration_minutes || ''}
                      onChange={(e) => setFormData({ ...formData, duration_minutes: parseInt(e.target.value) || undefined })}
                    />
                  </div>
                </div>
              )}

              {/* Email-specific fields */}
              {activeTab === 'EMAIL' && (
                <div className="space-y-2">
                  <Label>{t('crm.emailDirection', 'Direction')}</Label>
                  <Select
                    value={formData.email_direction || ''}
                    onValueChange={(v) => setFormData({ ...formData, email_direction: v as 'SENT' | 'RECEIVED' })}
                  >
                    <SelectTrigger><SelectValue placeholder="Select..." /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="SENT">Sent</SelectItem>
                      <SelectItem value="RECEIVED">Received</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              )}

              {/* Meeting-specific fields */}
              {activeTab === 'MEETING' && (
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>{t('crm.location', 'Location')}</Label>
                    <Input
                      value={formData.location || ''}
                      onChange={(e) => setFormData({ ...formData, location: e.target.value })}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>{t('crm.duration', 'Duration (min)')}</Label>
                    <Input
                      type="number"
                      value={formData.duration_minutes || ''}
                      onChange={(e) => setFormData({ ...formData, duration_minutes: parseInt(e.target.value) || undefined })}
                    />
                  </div>
                </div>
              )}

              {/* Task-specific fields */}
              {activeTab === 'TASK' && (
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>{t('crm.dueDate', 'Due Date')}</Label>
                    <Input
                      type="datetime-local"
                      value={formData.due_date?.slice(0, 16) || ''}
                      onChange={(e) => setFormData({ ...formData, due_date: e.target.value ? new Date(e.target.value).toISOString() : undefined })}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>{t('crm.priority', 'Priority')}</Label>
                    <Select
                      value={formData.priority || ''}
                      onValueChange={(v) => setFormData({ ...formData, priority: v as ActivityPriority })}
                    >
                      <SelectTrigger><SelectValue placeholder="Select..." /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="LOW">Low</SelectItem>
                        <SelectItem value="MEDIUM">Medium</SelectItem>
                        <SelectItem value="HIGH">High</SelectItem>
                        <SelectItem value="URGENT">Urgent</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              )}
            </div>
          </Tabs>

          <DialogFooter className="mt-6">
            <Button type="button" variant="outline" onClick={() => setOpen(false)}>
              {t('common.cancel', 'Cancel')}
            </Button>
            <Button type="submit" disabled={createMutation.isPending}>
              {createMutation.isPending && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              {t('common.save', 'Save')}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}

export default QuickActivityLog

